import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CreditCard, ShieldCheck, ArrowLeft, FileText, Users } from "lucide-react";
import { api, apiUrl } from "../lib/api";
import { getUTM } from "../lib/utm";
import { buildTrackingPayload } from "../lib/clickIds";
import { preselectOutcome, sectionLabel, showsStrip } from "../lib/checkoutGroup";
import { useCheckoutMode, useSettings, useActivePaymentBranding } from "../lib/settings";
import {
  trackBeginCheckout,
  trackAddPaymentInfo,
  trackRegistrationSubmit,
} from "../lib/tracking";
import Header from "../components/Header";
import Footer from "../components/Footer";
import NetopiaPaymentsBanner from "../components/NetopiaPaymentsBanner";
import GroupSelector from "../components/GroupSelector";
import ChosenGroupCard from "../components/ChosenGroupCard";
import GroupSessionsList from "../components/GroupSessionsList";
import TurnstileField from "../components/TurnstileField";
import { notifyLeadSubmitted } from "../components/ExitIntent";
import ReferralCodeField from "../components/ReferralCodeField";
import { acceptsPrima, checkoutPrice, lockedSchool, priceText } from "../lib/checkoutPrice";
import { isFped } from "../lib/regime";
import SchoolSelector from "../components/SchoolSelector";
import TeacherProfileFields, { useTeacherVocab } from "../components/TeacherProfileFields";
import { FALLBACK_RULE, targetGroupState } from "../lib/targetGroup";
import CountyCombobox from "../components/forms/CountyCombobox";

// Course preferences (M730). Both lists end with the "Altă variantă" escape
// hatch — when selected, the user must fill in a free-text description.
const PREFERENCE_OTHER = "Altă variantă";
const PREFERRED_COURSE_DAYS = [
  "Doar în cursul săptămânii",
  "Doar sâmbăta",
  "În cursul săptămânii și sâmbăta",
  PREFERENCE_OTHER,
];
const PREFERRED_TIME_OF_DAY = ["Dimineață", "Prânz", "Seară", PREFERENCE_OTHER];

const initial = {
  nume: "", prenume: "", email: "", telefon: "",
  specializare: "", unitate: "",
  city: "", county: "",
  // Schools directory v1 — populated by <SchoolSelector />.
  uat_name: "",
  school_code: null,
  city_original: "",
  manual_school_entry: false,
  // Teacher profile v1
  teaching_levels: [],
  teaching_level_other: "",
  specialization: "",
  specialization_other: "",
  // Course-day & time-of-day preferences (collected only when no active cohort).
  preferred_course_days: "",
  preferred_course_days_other: "",
  preferred_time_of_day: "",
  preferred_time_of_day_other: "",
  consent_privacy: false,
  consent_marketing: false,
};

/**
 * Netopia v2 uses a hosted payment page reachable via a single redirect URL
 * returned by the backend. There is no longer a hidden auto-submit form.
 */

export default function Checkout({ linkCode = null }) {
  const [form, setForm] = useState(initial);
  // A cohort reached only through its own link, `/inscriere/<code>` (07.10.2026):
  // that cohort and no other, with who it is for and who pays. `null` until
  // known; `{ error }` when the link is wrong, replaced or paused.
  const [linkGroup, setLinkGroup] = useState(null);
  // The accredited target group (02.10.2026): "Universitar" without any
  // pre-university cycle stops the checkout here and on the server.
  const teacherVocab = useTeacherVocab();
  const targetGroupRule = teacherVocab.target_group || FALLBACK_RULE;
  const outsideTargetGroup = targetGroupState(form.teaching_levels, targetGroupRule) === "blocked";
  // A cohort picked on the homepage schedule arrives as `?grupa=<id>`, so the
  // choice is not made twice. Read in the initialiser rather than an effect:
  // `GroupSelector` captures this value when it mounts, in order to echo the
  // matching cohort object back once its list loads. Set from an effect, it
  // would arrive a render too late and the selection would be dropped.
  //
  // Nothing is trusted here beyond the string itself — an id that matches no
  // open cohort is cleared by that same echo.
  const [groupId, setGroupId] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get("grupa") || null;
    } catch {
      return null;
    }
  });
  // The public cohort list, fetched here rather than inside `GroupSelector`:
  // the page has to know whether the preselected id still names an open cohort
  // before it decides whether to show the list at all. `null` while loading.
  const [groups, setGroups] = useState(null);
  const [groupsError, setGroupsError] = useState(null);
  // Whether the full list is open. A cohort chosen on the homepage is
  // confirmed instead, with the list one click away (29.09.2026).
  const [listOpen, setListOpen] = useState(false);
  // Why a preselected cohort was dropped — said out loud above the list,
  // because the selection used to disappear in silence.
  const [dropNote, setDropNote] = useState("");
  // When the public `/groups/active` list returns empty, we still allow
  // registration_only submits and the backend will mark the enrollment with
  // `needs_group_assignment=true` so admins can attribute a cohort later.
  const [noActiveGroups, setNoActiveGroups] = useState(false);
  // The referral code a colleague gave, typed in `ReferralCodeField`. The
  // server leaves it out when it cannot be used or the offer is off.
  const [referralCode, setReferralCode] = useState("");
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false); // true while we redirect to Netopia
  const [countyInvalid, setCountyInvalid] = useState(false);

  // M847 — payment-only mode (admin sent a link). Detected from ?enrollment_id=...
  const [paymentOnly, setPaymentOnly] = useState(null);
  // `paymentOnly` shape:
  //   null     → not yet decided (initial render)
  //   { status: "loading" }
  //   { status: "ready", enrollment, payment } → render the slim Pay page
  //   { status: "error", message }
  //   { status: "paid", enrollment }           → friendly "already paid" UI
  const turnstileRef = useRef(null);
  // Set immediately before we hand the browser to the payment page.
  //
  // `beforeunload` fires on THAT navigation too, so without this every single
  // person who went to pay was marked "abandoned" on the way out. A successful
  // payment then overwrote it, but a declined one did not — so the dashboard
  // showed "abandoned" for people whose card was actually refused, and the two
  // were indistinguishable. Of 16 such rows on 10–11 September, 11 were
  // declines and only 5 were real.
  //
  // Declared here rather than beside the beacon below, because `handlePayNow`
  // reads it and is defined above that point.
  const leavingForPaymentRef = useRef(false);
  const navigate = useNavigate();
  const settings = useSettings();
  const mode = useCheckoutMode();
  // A cohort paid by an institution takes nothing at enrolment, whatever the
  // site's own mode; one paid by each participant follows the site.
  const institutionPays = Boolean(linkCode) && linkGroup?.billing_mode === "institution";
  const isOnline = institutionPays ? false : mode.isOnline;
  const isRegistration = institutionPays ? true : mode.isRegistration;
  const requiresGroup = linkCode ? true : mode.requiresGroup;
  const { loaded } = mode;
  // Whether a cohort must be chosen, derived once so validation and rendering
  // can never disagree — showing required preference fields the submit check
  // ignores, or rejecting a submit over fields that were never displayed.
  //
  //   online_payment            → always required
  //   online_payment_no_group   → never required; preferences take its place
  //   registration_only         → required only while active cohorts exist
  const groupRequired = requiresGroup && !(isRegistration && noActiveGroups);
  const showPreferences = !groupId && !groupRequired;
  // The chosen cohort, and what to do about it. Read from the live `groupId`,
  // so a cohort picked from the list is confirmed the same way as one that
  // arrived in the address.
  const preselect = preselectOutcome(groups, groupId, Boolean(groupsError));
  const chosenGroup = preselect.state === "chosen" ? preselect.group : null;
  const strip = showsStrip(preselect, listOpen);
  // What this checkout charges: the cohort's own price when it has one, else
  // the site's (09.10.2026). The server decides the amount; this shows it.
  const pricedGroup = linkCode ? (linkGroup && !linkGroup.error ? linkGroup : null) : chosenGroup;
  const price = checkoutPrice(pricedGroup, settings.price);
  // The Prima de Carieră Didactică card: mentioned unless the cohort has it off (12.10.2026).
  const prima = acceptsPrima(pricedGroup);
  // A Curs FPED cohort, reached through its link: the page does not speak of the accreditation.
  const fped = Boolean(linkCode) && isFped(linkGroup);
  // A cohort made for one school carries the school, filled in and locked (12.10.2026).
  const schoolLock = lockedSchool(linkCode ? linkGroup : null);
  useEffect(() => {
    if (schoolLock) setForm((f) => (f.unitate === schoolLock ? f : { ...f, unitate: schoolLock }));
  }, [schoolLock]);

  // The list, fetched once. `GroupSelector` used to own this, and with it the
  // only answer to "does this id still name an open cohort".
  useEffect(() => {
    let active = true;
    if (linkCode) {
      api.get(`/groups/by-link/${encodeURIComponent(linkCode)}`)
        .then((r) => {
          if (!active) return;
          setLinkGroup(r.data);
          setGroups([r.data]);
          setGroupId(r.data.id);
          setNoActiveGroups(false);
        })
        .catch((e) => {
          if (active) setLinkGroup({ error: e?.response?.data?.detail || "Linkul nu mai e valabil." });
        });
      return () => { active = false; };
    }
    api.get("/groups/active")
      .then((r) => {
        if (!active) return;
        setGroups(r.data || []);
        // Drives submit validation: registration_only accepts a group-less
        // enrolment while there is nothing to choose from.
        setNoActiveGroups((r.data || []).length === 0);
      })
      .catch((e) => {
        if (active) setGroupsError(e?.response?.data?.detail || "Nu am putut încărca grupele.");
      });
    return () => { active = false; };
  }, [linkCode]);

  // A cohort that filled up, closed its enrolments or left the site between
  // the click on the homepage and this form: the id goes, so nothing submits a
  // cohort the backend would refuse, and the reason stays on screen.
  useEffect(() => {
    if (preselect.state !== "dropped") return;
    setDropNote(preselect.note);
    setGroupId(null);
  }, [preselect.state, preselect.note]);

  const chooseGroup = (id) => {
    setGroupId(id || null);
    setDropNote("");
    // Decided again — the strip takes over, with the new cohort in it.
    if (id) setListOpen(false);
  };
  // M901 — single source of truth for provider-aware copy ("Netopia Payments"
  // vs "EuPlătesc" vs neutral). All hardcoded "Netopia" mentions below were
  // replaced with `branding.*` reads.
  const branding = useActivePaymentBranding();

  // M847 — Detect `?enrollment_id=...` once on mount. If present we switch
  // to payment-only mode and skip the regular checkout form entirely.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const eid = params.get("enrollment_id");
    if (!eid) return;
    setPaymentOnly({ status: "loading" });
    (async () => {
      try {
        const res = await api.get(`/enrollments/status/${eid}`);
        const enr = res.data || {};
        const payment = enr.payment || null;
        if (enr.status === "paid" || payment?.status === "paid") {
          setPaymentOnly({ status: "paid", enrollment: enr, payment });
          return;
        }
        setPaymentOnly({ status: "ready", enrollment: enr, payment });
      } catch (err) {
        setPaymentOnly({
          status: "error",
          message: err?.response?.data?.detail
            || "Nu am putut găsi înscrierea. Te rugăm să verifici linkul sau să ne contactezi.",
        });
      }
    })();
  }, []);

  // M847 — Click "Plătește acum" → ask backend for a fresh Netopia URL.
  const handlePayNow = async () => {
    if (!paymentOnly || paymentOnly.status !== "ready") return;
    const eid = paymentOnly.enrollment?.id;
    if (!eid) return;
    setLoading(true);
    try {
      const res = await api.post(`/enrollments/${eid}/start-payment`);
      const { payment_url } = res.data;
      if (!payment_url) {
        throw new Error("Lipsește URL-ul de plată în răspunsul Netopia.");
      }
      setRedirecting(true);
      leavingForPaymentRef.current = true;
      window.location.href = payment_url;
      setTimeout(() => {
        window.location.href = `/cancel?enrollment_id=${eid}`;
      }, 15000);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail
          || err?.message
          || "Nu am putut iniția plata. Te rugăm să încerci din nou.",
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOnline && price) {
      trackBeginCheckout(price, settings.currency);
    }
  }, [isOnline, price, settings.currency]);

  const submit = async (e) => {
    e.preventDefault();
    if (outsideTargetGroup) {
      toast.error(targetGroupRule.message);
      return;
    }
    if (!form.consent_privacy) {
      toast.error("Te rugăm să accepți Politica de confidențialitate și Termenii și condițiile.");
      return;
    }
    if (!form.consent_marketing) {
      toast.error("Pentru a putea procesa înscrierea, avem nevoie de acordul tău de comunicare.");
      return;
    }
    if (!form.county) {
      setCountyInvalid(true);
      toast.error("Te rugăm să selectezi județul.");
      return;
    }
    if (!groupId && groupRequired) {
      // Group required for online_payment ALWAYS (we need a snapshot for the
      // payment). Group required for registration_only ONLY when there are
      // active cohorts to choose from — otherwise we accept group-less submit.
      toast.error(
        isOnline
          ? "Te rugăm să alegi o grupă pentru înscriere."
          : "Te rugăm să alegi o grupă pentru înscriere."
      );
      return;
    }
    // M730: preferences are required when the user submits without a group
    // (the only branch where the UI surfaces them). Mirror the backend
    // validator so we fail fast in the browser with a clear message.
    if (!groupRequired && !groupId) {
      if (!form.preferred_course_days) {
        toast.error("Te rugăm să alegi când ai prefera să participi la curs.");
        return;
      }
      if (form.preferred_course_days === PREFERENCE_OTHER
          && !form.preferred_course_days_other.trim()) {
        toast.error('Pentru „Altă variantă" la zilele de curs, te rugăm să descrii preferința ta.');
        return;
      }
      if (!form.preferred_time_of_day) {
        toast.error("Te rugăm să alegi în ce parte a zilei ai prefera să participi.");
        return;
      }
      if (form.preferred_time_of_day === PREFERENCE_OTHER
          && !form.preferred_time_of_day_other.trim()) {
        toast.error('Pentru „Altă variantă" la intervalul orar, te rugăm să descrii intervalul preferat.');
        return;
      }
    }
    if (!turnstileToken) {
      toast.error("Te rugăm să aștepți verificarea anti-spam.");
      return;
    }
    setLoading(true);
    try {
      const utm = getUTM();
      // PR-T1/T3: tracking sidecar (event_id, fbclid/gclid/ttclid, _fbp/_fbc, _ga).
      const tracking = buildTrackingPayload();
      // Var B+: `group_id` is optional for registration_only. When the public
      // `/groups/active` list is empty, we send `null` and the backend marks
      // the enrollment `needs_group_assignment=true`.
      const body = {
        ...form,
        group_id: groupId || null,
        source: "checkout",
        utm,
        ...tracking,
        turnstile_token: turnstileToken,
        // The code a colleague gave: only from the checkout of the site, and
        // left out by the server when it cannot be used or the offer is off.
        referral_code: !linkCode && (referralCode || "").trim() ? referralCode.trim().toUpperCase() : null,
        ...(linkCode ? { link_code: linkCode } : {}),
      };
      if (isOnline) {
        trackAddPaymentInfo(price || 0, settings.currency);
        const res = await api.post("/enrollments/initiate-payment", body);
        const { payment_url, purchase_id, enrollment_id } = res.data;
        lastPurchaseIdRef.current = purchase_id;
        lastEnrollmentIdRef.current = enrollment_id;
        notifyLeadSubmitted();
        setRedirecting(true);
        // Leaving on purpose — not an abandonment. The purchase stays
        // `pending_payment` until the provider tells us what happened.
        leavingForPaymentRef.current = true;
        // Hand the browser off to the provider's hosted payment page.
        window.location.href = payment_url;
        // Safety fallback: if the redirect somehow does not happen
        // (e.g. popup blocker), bounce to /cancel after 15s so the
        // user is never stuck on an opaque loader.
        setTimeout(() => { window.location.href = `/cancel?purchase_id=${purchase_id}`; }, 15000);
      } else {
        // Registration-only flow: persist server-side first, then fire the
        // browser Pixel with the SAME event_id so Meta dedup pairs them.
        // Firing AFTER the POST avoids two failure modes:
        //   (a) Pixel firing on a POST that ends up 4xx (server then never
        //       sends CAPI → orphan browser event, no dedup)
        //   (b) browser-side race where `navigate()` cancels the in-flight
        //       Pixel img-beacon before it hits Meta's edge.
        const res = await api.post("/enrollments/submit", body);
        // PR-T3 — fire CompleteRegistration with the SAME event_id sent to backend.
        // value/currency must be present so Pixel JS serialises the options
        // object correctly (empty params {} can drop {eventID} on some builds).
        trackRegistrationSubmit({ event_id: tracking.event_id }, tracking.event_id, 0, "RON");
        notifyLeadSubmitted();
        toast.success("Înscrierea ta a fost înregistrată!");
        // Give the Pixel img-beacon ~150ms to reach Meta's CDN before the
        // SPA route change unmounts the page (defence in depth — fbq uses a
        // GET on graph.facebook.com/tr which is fast but not instant).
        await new Promise((resolve) => setTimeout(resolve, 150));
        navigate(`/success?enrollment_id=${res.data.enrollment_id}&mode=registration`);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "A apărut o eroare. Te rugăm să încerci din nou.");
      setTurnstileToken(null);
      turnstileRef.current?.reset();
      setLoading(false);
    }
  };

  // Abandon-cart beacon (Var B): minimal payload — just identifiers.
  const lastPurchaseIdRef = useRef(null);
  const lastEnrollmentIdRef = useRef(null);
  useEffect(() => {
    if (!isOnline) return;
    const before = () => {
      if (leavingForPaymentRef.current) return;
      const pid = lastPurchaseIdRef.current;
      const eid = lastEnrollmentIdRef.current;
      if (!pid && !eid) return;
      const payload = {
        purchase_id: pid || undefined,
        enrollment_id: eid || undefined,
        email: form.email || undefined,
      };
      navigator.sendBeacon?.(
        apiUrl("/checkout/abandon"),
        new Blob([JSON.stringify(payload)], { type: "application/json" })
      );
    };
    window.addEventListener("beforeunload", before);
    return () => window.removeEventListener("beforeunload", before);
  }, [form, isOnline]);

  if (redirecting) {
    return (
      <div className="min-h-screen bg-[#F5F6F6] grid place-items-center">
        <div className="text-center" data-testid="netopia-redirect-loader">
          <Loader2 className="animate-spin mx-auto text-[#965EA7]" size={36} />
          <p className="mt-4 text-[#373A45] font-semibold">{branding.redirectingText}</p>
          <p className="mt-1 text-sm text-[#6B7280]">Te rugăm să nu închizi această fereastră.</p>
        </div>
      </div>
    );
  }

  const submitLabel = isOnline
    ? (price != null ? `Plătește ${priceText(price, settings.currency)}` : "Plătește")
    : "Trimite înscrierea";
  // Three shapes, not two. Paying WITH a cohort keeps the commerce wording —
  // the buyer picked a concrete series and is completing an order. Paying
  // WITHOUT one is an enrolment whose scheduling is still open, so the title
  // says so rather than implying a finished transaction.
  const formTitle = linkCode && linkGroup?.institution_name
    ? `Înscriere · ${linkGroup.institution_name}`
    : !isOnline
    ? "Înscriere la curs"
    : requiresGroup
      ? "Finalizare comandă"
      : "Finalizează înscrierea la curs";
  const formSubtitle = fped
    ? "Te înscrii în grupa dedicată de mai jos, la cursul „Introducere în Inteligența Artificială pentru educație”, "
      + "organizat de Fundația pentru Educație Digitală."
    : linkCode
    ? "Te înscrii în grupa dedicată de mai jos. Cursul „Introducere în Inteligența Artificială pentru educație” "
      + "este acreditat de Ministerul Educației și Cercetării prin OMEC nr. 4712/19.08.2026."
    : isOnline
    ? "Alege grupa potrivită pentru tine, apoi completează datele. Cursul "
      + "„Introducere în Inteligența Artificială pentru educație” este acreditat de "
      + "Ministerul Educației și Cercetării prin OMEC nr. 4712/19.08.2026."
    : "Completează formularul de mai jos pentru a te înscrie la curs. Echipa noastră va reveni către tine cu detaliile de plată și pașii următori.";

  // M847 — Payment-only mode (link sent by admin). We never render the full
  // registration form here; the user already submitted it once. Just show a
  // summary + a single Pay button.
  if (paymentOnly) {
    return (
      <PaymentOnlyView
        state={paymentOnly}
        loading={loading}
        onPay={handlePayNow}
        currency={settings.currency || "RON"}
      />
    );
  }

  // The link's own pages: still loading, wrong, or the cohort can take nobody.
  if (linkCode && (!linkGroup || linkGroup.error || !linkGroup.enrolment_open || linkGroup.is_full)) {
    return <LinkStatePage linkGroup={linkGroup} />;
  }

  return (
    // M913B — Selective PII masking. Form CONTAINER is no longer masked
    // so labels, dropdowns, validation messages and CTAs remain visible
    // in Clarity recordings. Only individual PII inputs (name, email,
    // phone, school, free-text "Other") carry `clarity-mask`.
    <div className="min-h-screen bg-[#F5F6F6]">
      <Header />
      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-14" data-testid="checkout-page">
        <button onClick={() => navigate(-1)} className="text-sm text-[#6B7280] hover:text-[#965EA7] inline-flex items-center gap-1 mb-6">
          <ArrowLeft size={14} /> Înapoi
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <form onSubmit={submit} className="lg:col-span-8 bg-white rounded-3xl border border-[#E5E7EB] p-7 md:p-10" data-testid="checkout-form">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#F0E7F4] px-3 py-1 text-xs font-semibold text-[#965EA7] uppercase tracking-widest">
              {isOnline ? "Plată online securizată" : "Înscriere la curs"}
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-[#373A45] mt-3">{formTitle}</h1>
            <p className="text-[#6B7280] mt-1">{formSubtitle}</p>

            {institutionPays && (
              <div className="mt-5 bg-[#EAF4EE] border border-[#519872]/25 rounded-2xl p-4 text-sm text-[#373A45]" data-testid="checkout-institution-banner">
                <div className="flex gap-2 items-start">
                  <FileText size={16} className="text-[#3c7457] mt-0.5 shrink-0" />
                  <p className="leading-relaxed">
                    Taxa de participare este acoperită de <strong className="font-semibold">{linkGroup.institution_name}</strong>.
                    <span className="block mt-2 font-semibold text-[#3c7457]">Nu ai nimic de plătit.</span>
                    <span className="block mt-2">
                      După ce trimiți înscrierea, echipa FPED o confirmă și primești pe email pașii următori:
                      datele pentru dosar, apoi contractul de semnat.
                    </span>
                  </p>
                </div>
              </div>
            )}

            {loaded && isRegistration && !linkCode && (
              <div className="mt-5 bg-[#F0E7F4] border border-[#965EA7]/20 rounded-2xl p-4 text-sm text-[#373A45]" data-testid="checkout-registration-banner">
                <div className="flex gap-2 items-start">
                  <FileText size={16} className="text-[#965EA7] mt-0.5 shrink-0" />
                  <p className="leading-relaxed">
                    Aceasta este o <strong className="font-semibold">înscriere</strong> la curs.{" "}
                    {prima && (
                      <span data-testid="checkout-registration-prima">
                        Programul este acreditat prin OMEC nr. 4712/19.08.2026 și poate fi decontat
                        prin cardul Prima de Carieră Didactică.{" "}
                      </span>
                    )}
                    Echipa noastră va reveni către tine cu detaliile de plată și pașii următori.
                    <span className="block mt-2 text-[#7A4986] font-semibold">
                      Plata nu se face în acest formular.
                    </span>
                  </p>
                </div>
              </div>
            )}

            {/* Paying without picking a cohort: the participant sees no date
                anywhere on this page before pressing "Plătește", so the page has
                to say plainly what they are buying and what happens next. */}
            {loaded && isOnline && !requiresGroup && (
              <div className="mt-5 bg-[#F0E7F4] border border-[#965EA7]/20 rounded-2xl p-4 text-sm text-[#373A45]" data-testid="checkout-no-group-banner">
                <div className="flex gap-2 items-start">
                  <FileText size={16} className="text-[#965EA7] mt-0.5 shrink-0" />
                  <p className="leading-relaxed">
                    Îți rezervi locul la curs acum. Programul este acreditat prin
                    OMEC nr. 4712/19.08.2026 și poate fi plătit prin cardul
                    Prima de Carieră Didactică.
                    <span className="block mt-2">
                      După înscriere, te vom contacta pentru a stabili împreună varianta
                      de program care ți se potrivește cel mai bine. Grupele se formează
                      în funcție de preferințele participanților, iar înainte de începerea
                      cursului vei primi toate detaliile și calendarul complet de desfășurare.
                    </span>
                  </p>
                </div>
              </div>
            )}

            {settings.checkout_intro_text && !linkCode && (
              <div className="mt-5 text-sm text-[#6B7280] whitespace-pre-wrap">{settings.checkout_intro_text}</div>
            )}

            <div className="mt-6">
              <div className="flex items-baseline justify-between mb-3">
                <span className="text-xs font-semibold text-[#373A45] uppercase tracking-widest">
                  {linkCode ? "Grupa dedicată" : sectionLabel(strip)}
                  {/* Nothing left to choose once the choice is confirmed, so the
                      required-field asterisk goes with the question. */}
                  {!strip && groupRequired && !linkCode && <span className="text-[#965EA7]"> *</span>}
                </span>
                {/* No wording when the cohort is mandatory — the asterisk says it,
                    exactly as on every other required field. Every branch here
                    used to open with "opțional", including where a cohort was in
                    fact required, so anyone who believed it hit an error only at
                    submit. Tied to `groupRequired`, the same value the submit
                    check reads, so label and behaviour cannot drift apart. */}
                {!groupRequired && !strip && !linkCode && (
                  <span className="text-xs text-[#6B7280]">
                    {!requiresGroup
                      ? "opțional · grupa se stabilește în funcție de preferințele tale"
                      : "opțional · nu sunt grupe active acum"}
                  </span>
                )}
              </div>
              {linkCode ? (
                <ChosenGroupCard group={linkGroup} />
              ) : preselect.state === "loading" ? (
                <div className="flex items-center gap-2 py-6 text-sm text-[#6B7280]" data-testid="checkout-group-loading">
                  <Loader2 size={16} className="animate-spin" /> Se încarcă grupa aleasă…
                </div>
              ) : strip ? (
                <ChosenGroupCard group={chosenGroup} onShowAll={() => setListOpen(true)} />
              ) : (
                <>
                  {dropNote && (
                    <div
                      className="mb-3 rounded-2xl border border-[#F59E0B]/40 bg-[#FDF3E7] px-4 py-3 text-sm text-[#8a6a1a]"
                      data-testid="checkout-group-drop-note"
                      role="status"
                    >
                      {dropNote}
                    </div>
                  )}
                  <GroupSelector
                    groups={groups}
                    error={groupsError}
                    value={groupId}
                    onChange={chooseGroup}
                    allowSkip={false}
                  />
                </>
              )}

              {/* Course preferences — surfaced ONLY when no active cohort
                  exists. Helps the team shape future groups around what
                  teachers actually want. Optional; never blocks submit. */}
              {showPreferences && (
                <div
                  className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4"
                  data-testid="checkout-preferences-block"
                >
                  <label className="block">
                    <span className="text-xs font-semibold text-[#373A45] uppercase tracking-widest">
                      Când ai prefera să participi la curs? *
                    </span>
                    <div className="mt-1.5">
                      <CountyCombobox
                        options={PREFERRED_COURSE_DAYS}
                        value={form.preferred_course_days}
                        onChange={(v) => setForm({
                          ...form,
                          preferred_course_days: v,
                          // Reset the custom text when leaving "Altă variantă"
                          // so a stale value doesn't ride along.
                          preferred_course_days_other: v === PREFERENCE_OTHER
                            ? form.preferred_course_days_other
                            : "",
                        })}
                        placeholder="Selectează preferința"
                        searchPlaceholder="Caută preferință..."
                        emptyMessage="Niciun rezultat."
                        testId="checkout-preferred-days"
                      />
                    </div>
                    {form.preferred_course_days === PREFERENCE_OTHER && (
                      <div className="mt-2 transition-all" data-testid="checkout-preferred-days-other-wrap">
                        <input
                          type="text"
                          required
                          maxLength={200}
                          value={form.preferred_course_days_other}
                          onChange={(e) => setForm({ ...form, preferred_course_days_other: e.target.value })}
                          placeholder="ex. duminica, program flexibil, intensiv etc."
                          className="w-full bg-[#F5F6F6] border border-[#E5E7EB] rounded-xl px-4 py-3 text-[#373A45] focus:ring-2 focus:ring-[#965EA7] focus:border-transparent outline-none text-sm"
                          data-testid="checkout-preferred-days-other"
                          autoComplete="off"
                          spellCheck={false}
                          aria-label="Descrie preferința ta"
                        />
                      </div>
                    )}
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold text-[#373A45] uppercase tracking-widest">
                      În ce parte a zilei ai prefera să participi? *
                    </span>
                    <div className="mt-1.5">
                      <CountyCombobox
                        options={PREFERRED_TIME_OF_DAY}
                        value={form.preferred_time_of_day}
                        onChange={(v) => setForm({
                          ...form,
                          preferred_time_of_day: v,
                          preferred_time_of_day_other: v === PREFERENCE_OTHER
                            ? form.preferred_time_of_day_other
                            : "",
                        })}
                        placeholder="Selectează intervalul"
                        searchPlaceholder="Caută interval..."
                        emptyMessage="Niciun rezultat."
                        testId="checkout-preferred-time"
                      />
                    </div>
                    {form.preferred_time_of_day === PREFERENCE_OTHER && (
                      <div className="mt-2 transition-all" data-testid="checkout-preferred-time-other-wrap">
                        <input
                          type="text"
                          required
                          maxLength={200}
                          value={form.preferred_time_of_day_other}
                          onChange={(e) => setForm({ ...form, preferred_time_of_day_other: e.target.value })}
                          placeholder="ex. după ora 20:00, variabil, weekend etc."
                          className="w-full bg-[#F5F6F6] border border-[#E5E7EB] rounded-xl px-4 py-3 text-[#373A45] focus:ring-2 focus:ring-[#965EA7] focus:border-transparent outline-none text-sm"
                          data-testid="checkout-preferred-time-other"
                          autoComplete="off"
                          spellCheck={false}
                          aria-label="Descrie intervalul preferat"
                        />
                      </div>
                    )}
                  </label>
                  <p
                    className="md:col-span-2 text-xs text-[#6B7280] leading-relaxed"
                    data-testid="checkout-preferences-help"
                  >
                    Aceste preferințe ne ajută să organizăm următoarele grupe, dar nu reprezintă o rezervare fermă.
                  </p>
                </div>
              )}
            </div>
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                ["prenume", "Prenume", true, "text", { autoComplete: "given-name", autoCapitalize: "words" }],
                ["nume", "Nume", true, "text", { autoComplete: "family-name", autoCapitalize: "words" }],
                ["email", "Email", true, "email", {
                  autoComplete: "email", inputMode: "email",
                  autoCapitalize: "none", autoCorrect: "off", spellCheck: false,
                }],
                ["telefon", "Telefon", true, "tel", { autoComplete: "tel", inputMode: "tel" }],
              ].map(([k, label, req, type, extra]) => (
                <label key={k} htmlFor={`checkout-${k}`} className="block">
                  <span className="text-xs font-semibold text-[#373A45] uppercase tracking-widest">
                    {label}{req && <span className="text-[#965EA7]"> *</span>}
                  </span>
                  <input
                    id={`checkout-${k}`}
                    name={k}
                    required={req}
                    type={type}
                    value={form[k]}
                    onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="mt-1.5 w-full bg-[#F5F6F6] border border-[#E5E7EB] rounded-xl px-4 py-3 text-[#373A45] focus:ring-2 focus:ring-[#965EA7] focus:border-transparent outline-none clarity-mask"
                    data-clarity-mask="true"
                    data-testid={`checkout-input-${k}`}
                    {...extra}
                  />
                </label>
              ))}
            </div>

            <div className="mt-6">
              <TeacherProfileFields
                value={{
                  teaching_levels: form.teaching_levels,
                  teaching_level_other: form.teaching_level_other,
                  specialization: form.specialization,
                  specialization_other: form.specialization_other,
                }}
                onChange={(patch) => setForm((f) => {
                  const next = { ...f, ...patch };
                  // Mirror the structured spec into the legacy `specializare`
                  // so the older CRM views keep working.
                  if ("specialization" in patch || "specialization_other" in patch) {
                    const spec = patch.specialization ?? f.specialization;
                    const other = patch.specialization_other ?? f.specialization_other;
                    next.specializare = spec === "Altă disciplină" ? (other || "") : (spec || "");
                  }
                  return next;
                })}
                required
                targetGroup="block"
              />
            </div>

            <div className="mt-6">
              <SchoolSelector
                value={{
                  county: form.county,
                  uat_name: form.uat_name,
                  school_name: form.unitate,
                  school_code: form.school_code,
                  city_original: form.city_original,
                  manual_school_entry: form.manual_school_entry,
                }}
                lockedSchool={schoolLock}
                onChange={(patch) => {
                  // Map SchoolSelector's vocabulary back to the legacy form
                  // field names that the backend already understands. We ALSO
                  // mirror `uat_name` into `city` so legacy CRM views still
                  // show a sensible "Localitate" column.
                  setForm((f) => {
                    const next = { ...f };
                    if ("county" in patch) {
                      next.county = patch.county;
                      setCountyInvalid(false);
                    }
                    if ("uat_name" in patch) {
                      next.uat_name = patch.uat_name;
                      next.city = patch.uat_name || "";
                    }
                    // Picking a county or a locality clears the school; a locked one stays.
                    if ("school_name" in patch) next.unitate = schoolLock || patch.school_name;
                    if ("school_code" in patch) next.school_code = patch.school_code;
                    if ("city_original" in patch) next.city_original = patch.city_original;
                    if ("manual_school_entry" in patch) next.manual_school_entry = patch.manual_school_entry;
                    return next;
                  });
                }}
                required
              />
            </div>


            {!linkCode && <ReferralCodeField code={referralCode} setCode={setReferralCode} email={form.email} />}

            <div className="mt-5">
              <TurnstileField
                ref={turnstileRef}
                onVerify={setTurnstileToken}
                onExpire={() => setTurnstileToken(null)}
                onError={() => setTurnstileToken(null)}
              />
            </div>

            {isOnline && (
              <div className="mt-6 bg-[#EDEEF0] border border-[#E5E7EB] rounded-2xl p-5 text-sm text-[#373A45]">
                <div className="flex items-center gap-2 font-semibold text-[#965EA7]">
                  <CreditCard size={16} /> {branding.buttonHelperText}
                </div>
                <div className="mt-2 text-[#6B7280]">
                  {branding.inlineNoticeText}
                  {prima && <span data-testid="checkout-prima-notice"> Acceptăm și cardul Prima de carieră didactică.</span>}
                </div>
              </div>
            )}

            <div className="mt-5 space-y-3">
              <label className="flex items-start gap-3 text-sm text-[#373A45]">
                <input
                  type="checkbox"
                  checked={form.consent_privacy}
                  onChange={(e) => setForm({ ...form, consent_privacy: e.target.checked })}
                  className="mt-1 h-4 w-4 accent-[#965EA7]"
                  required
                  data-testid="checkout-consent-privacy"
                />
                <span>
                  Am citit și sunt de acord cu{" "}
                  <Link to="/termeni-conditii" target="_blank" className="underline text-[#965EA7] hover:text-[#7A4986]">
                    Termenii și condițiile
                  </Link>{" "}
                  și{" "}
                  <Link to="/politica-confidentialitate" target="_blank" className="underline text-[#965EA7] hover:text-[#7A4986]">
                    Politica de confidențialitate
                  </Link>. <span className="text-[#965EA7]">*</span>
                </span>
              </label>
              <label className="flex items-start gap-3 text-sm text-[#373A45]">
                <input
                  type="checkbox"
                  checked={form.consent_marketing}
                  onChange={(e) => setForm({ ...form, consent_marketing: e.target.checked })}
                  className="mt-1 h-4 w-4 accent-[#965EA7]"
                  data-testid="checkout-consent-marketing"
                />
                <span>
                  Sunt de acord să primesc pe email/telefon comunicări legate de înscriere, confirmare, plată și alte programe educaționale organizate de Fundația pentru Educație Digitală. <span className="text-[#965EA7]">*</span>
                </span>
              </label>
            </div>

            {/* Mobile/tablet — trust signal directly above the submit CTA.
                Hidden on >=lg where the sidebar copy carries the banner.
                Always rendered (independent of payment mode) — Netopia is the
                eventual payment processor for both registration_only and
                online_payment flows, so the trust signal is always relevant. */}
            {!institutionPays && <NetopiaPaymentsBanner className="mt-6 lg:hidden" prima={prima} />}

            {/* What was chosen, restated where the decision is confirmed. The
                cohort is picked at the top of a long form; by the time someone
                reaches the pay button they have filled in three more sections
                and should not have to scroll back to check. */}
            {chosenGroup && (
              <div
                className="mt-5 rounded-2xl border border-[#965EA7]/25 bg-[#F0E7F4] px-4 py-3 text-sm"
                data-testid="checkout-selected-group-summary"
              >
                <div className="flex items-start gap-2">
                  <Users size={16} className="text-[#965EA7] mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="font-semibold text-[#373A45]">{chosenGroup.name}</div>
                    <div className="text-[#6B7280] mt-0.5">
                      {(chosenGroup.schedule_days || []).join(", ")}
                      {chosenGroup.schedule_time ? ` · ${chosenGroup.schedule_time}` : ""}
                    </div>
                    {chosenGroup.start_date && (
                      <div className="text-[#6B7280]">Start: {chosenGroup.start_date}</div>
                    )}
                    <GroupSessionsList sessions={chosenGroup.sessions} testid="checkout-group-sessions" className="mt-1.5" />
                  </div>
                </div>
              </div>
            )}

            {outsideTargetGroup && (
              <p className="mt-6 -mb-3 text-sm font-semibold text-[#a33a32]" data-testid="checkout-target-group-stop">
                {fped
                  ? "Înscrierea nu e disponibilă: cursul se adresează cadrelor didactice din învățământul preuniversitar."
                  : "Înscrierea nu e disponibilă: cursul e acreditat doar pentru învățământul preuniversitar."}
              </p>
            )}
            <button
              type="submit"
              disabled={loading || outsideTargetGroup}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white font-semibold text-base px-8 py-4 shadow-[0_4px_14px_0_rgba(150,94,167,0.39)] hover:-translate-y-0.5 transition-all disabled:opacity-60"
              data-testid="checkout-pay-button"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : <ShieldCheck size={18} />}
              {submitLabel}
            </button>
          </form>

          <aside className="lg:col-span-4">
            <div className="bg-[#373A45] text-white rounded-3xl p-7" data-testid="checkout-summary">
              <div className="text-white/70 text-xs uppercase tracking-widest">{isOnline ? "Comandă" : "Înscriere"}</div>
              <div className="mt-2 font-display text-xl font-semibold">
                Curs: Introducere în Inteligența Artificială pentru educație
              </div>
              <div className="mt-1 text-white/70 text-sm">10 module · 25 de ore (15 live + 10 individual) · acces 12 luni · certificat</div>
              {isOnline ? (
                <>
                  <div className="mt-6 flex items-end justify-between border-t border-white/10 pt-5">
                    <span className="text-white/70">Total</span>
                    <span className="font-display text-3xl font-bold">
                      {price != null ? priceText(price, settings.currency) : "—"}
                    </span>
                  </div>
                  <div className="mt-3 text-xs text-white/60">TVA inclus · facturare automată</div>
                  {prima && (
                    <div className="mt-6 inline-flex items-center gap-2 bg-white/10 rounded-full px-3 py-1.5 text-xs"
                      data-testid="checkout-prima-badge">
                      <CreditCard size={14} /> Card Prima de carieră didactică acceptat
                    </div>
                  )}
                </>
              ) : (
                <div className="mt-6 border-t border-white/10 pt-5 text-sm text-white/80 leading-relaxed">
                  {institutionPays
                    ? `Taxa de participare este acoperită de ${linkGroup.institution_name}. După confirmarea `
                      + "înscrierii primești pe email pașii următori și informațiile despre desfășurarea cursului."
                    : "Trimiterea formularului nu presupune nicio plată. După înregistrarea înscrierii, echipa "
                      + "noastră va reveni către tine cu detaliile de plată și cu informațiile despre desfășurarea cursului."}
                </div>
              )}
            </div>

            {/* Desktop only — trust signal sits in the sidebar gap exactly
                where the red rectangle in the design spec was drawn.
                Always rendered (independent of payment mode). */}
            {!institutionPays && <NetopiaPaymentsBanner className="mt-6 hidden lg:block" prima={prima} />}
          </aside>
        </div>
      </main>
      <Footer />
    </div>
  );
}


/**
 * M847 — payment-only view rendered when the URL carries `?enrollment_id=...`
 * (i.e. the user arrived from an admin-sent payment link). Renders a slim
 * summary card with a single "Plătește acum" CTA — never the full
 * registration form.
 */
function PaymentOnlyView({ state, loading, onPay, currency }) {
  // M901 — own branding read; this component is a sibling of <Checkout/>
  // and doesn't inherit the parent's `branding` closure.
  const branding = useActivePaymentBranding();
  if (state.status === "loading") {
    return (
      <div className="min-h-screen bg-[#F5F6F6]">
        <Header />
        <main className="max-w-3xl mx-auto px-6 py-20" data-testid="checkout-payment-only">
          <div className="bg-white rounded-3xl border border-[#E5E7EB] p-10 text-center">
            <Loader2 className="animate-spin mx-auto text-[#965EA7]" size={32} />
            <p className="mt-4 text-[#6B7280]">Încărcăm înscrierea ta…</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div className="min-h-screen bg-[#F5F6F6]">
        <Header />
        <main className="max-w-3xl mx-auto px-6 py-20" data-testid="checkout-payment-only">
          <div className="bg-white rounded-3xl border border-[#E5E7EB] p-10 text-center">
            <h1 className="font-display text-2xl font-bold text-[#373A45]">Link invalid</h1>
            <p className="mt-3 text-[#6B7280]">{state.message}</p>
            <Link to="/" className="mt-6 inline-flex rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white px-6 py-3 text-sm font-semibold">
              Înapoi la site
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  const enr = state.enrollment || {};
  const payment = state.payment || {};
  const snap = enr.group_snapshot || {};
  const amount = payment.amount ?? null;
  const cur = payment.currency || currency;
  const isAlreadyPaid = state.status === "paid";

  return (
    // M913B — Container is NOT masked: titles, summary labels (Nume,
    // Email, Grupa, Sumă) and CTAs remain visible. Only the actual values
    // (which contain PII like name/email/group) get `clarity-mask` via
    // the `<SummaryRow value>` rendering below.
    <div className="min-h-screen bg-[#F5F6F6]">
      <Header />
      <main className="max-w-3xl mx-auto px-6 lg:px-8 py-14" data-testid="checkout-payment-only">
        <div className="bg-white rounded-3xl border border-[#E5E7EB] p-7 md:p-10">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#F0E7F4] px-3 py-1 text-xs font-semibold text-[#965EA7] uppercase tracking-widest">
            {isAlreadyPaid ? "Înscriere achitată" : "Continuă plata cursului"}
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-[#373A45] mt-3">
            {isAlreadyPaid ? "Plata ta este deja confirmată" : "Finalizează plata"}
          </h1>
          <p className="text-[#6B7280] mt-1">
            {isAlreadyPaid
              ? "Mulțumim! Înscrierea ta a fost confirmată. Vei primi pe email toate detaliile despre curs."
              : `Înscrierea ta este pregătită. Apasă „Plătește acum" pentru a continua cu ${branding.securePaymentText.toLowerCase()}.`}
          </p>

          <dl className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <SummaryRow label="Nume" value={`${enr.prenume || ""} ${enr.nume || ""}`.trim() || "—"} />
            <SummaryRow label="Email" value={enr.email || "—"} />
            {snap.name && <SummaryRow label="Grupa" value={snap.name} />}
            {snap.start_date && <SummaryRow label="Start curs" value={snap.start_date} />}
            {snap.schedule_label && <SummaryRow label="Program" value={snap.schedule_label} />}
            {snap.format && <SummaryRow label="Format" value={snap.format} />}
            {amount != null && (
              <SummaryRow
                label="Sumă de plată"
                value={`${Number(amount).toLocaleString("ro-RO")} ${cur}`}
                highlight
              />
            )}
          </dl>

          {!isAlreadyPaid && (
            <>
              <button
                onClick={onPay}
                disabled={loading}
                className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#965EA7] hover:bg-[#7A4986] disabled:opacity-60 text-white px-7 py-4 text-base font-bold shadow-[0_4px_14px_0_rgba(150,94,167,0.39)] transition-all"
                data-testid="checkout-pay-now"
              >
                {loading ? (
                  <><Loader2 className="animate-spin" size={18} /> Se inițiază plata…</>
                ) : (
                  <><CreditCard size={18} /> Plătește acum {amount != null ? `· ${Number(amount).toLocaleString("ro-RO")} ${cur}` : ""}</>
                )}
              </button>
              <p className="mt-4 text-xs text-[#6B7280] inline-flex items-center gap-1.5">
                <ShieldCheck size={14} className="text-[#519872]" />
                {branding.securePaymentText}. Vei fi redirecționat către pagina lor.
              </p>
            </>
          )}

          {isAlreadyPaid && (
            <Link to="/" className="mt-8 inline-flex rounded-full bg-[#965EA7] hover:bg-[#7A4986] text-white px-6 py-3 text-sm font-semibold" data-testid="checkout-already-paid-cta">
              Înapoi la site
            </Link>
          )}
        </div>
        <NetopiaPaymentsBanner className="mt-6" prima={enr.prima_card !== false} />
      </main>
      <Footer />
    </div>
  );
}

function SummaryRow({ label, value, highlight = false }) {
  return (
    <>
      <dt className="text-[11px] uppercase tracking-widest text-[#6B7280] font-semibold">{label}</dt>
      <dd
        className={`text-sm clarity-mask ${highlight ? "font-bold text-[#373A45] text-lg" : "text-[#373A45]"}`}
        data-clarity-mask="true"
      >
        {value}
      </dd>
    </>
  );
}


/** The pages of a link that cannot take an enrolment: still loading, wrong
 *  or replaced, or a cohort that is full or closed (07.10.2026). */
function LinkStatePage({ linkGroup }) {
  const email = process.env.REACT_APP_CONTACT_EMAIL || "contact@fped.org";
  const loading = !linkGroup;
  const title = loading ? "" : linkGroup.error ? "Linkul nu mai e valabil"
    : linkGroup.is_full ? "Grupa e completă" : "Înscrierile s-au închis";
  const text = loading ? "" : linkGroup.error
    ? "Linkul a expirat, a fost înlocuit sau e greșit. Cere-l din nou instituției care ți l-a trimis sau scrie-ne."
    : linkGroup.is_full
      ? "Toate locurile din această grupă sunt ocupate. Dacă ar trebui să fii în ea, scrie-ne."
      : "Înscrierile la această grupă s-au închis. Dacă ar trebui să fii în ea, scrie-ne.";
  return (
    <div className="min-h-screen bg-[#F5F6F6]">
      <Header />
      <main className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="checkout-link-state">
        {loading ? (
          <Loader2 className="animate-spin mx-auto text-[#965EA7]" size={32} />
        ) : (
          <>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-[#373A45]">{title}</h1>
            <p className="mt-3 text-[#6B7280]">{text}</p>
            <p className="mt-6 text-sm text-[#373A45]">
              Fundația pentru Educație Digitală ·{" "}
              <a href={`mailto:${email}`} className="font-semibold text-[#965EA7] hover:underline">{email}</a>
            </p>
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}
